import React, { useState, useRef, useEffect } from "react";
import Modal from "../../components/Modal";
import InputField from "../../components/InputField";
import DropDown from "../../components/DropDown";
import Pagination from "../../components/Pagination";
import { useParentCodeData } from "./ParentCodeData";
import { entityActiveInactiveAction } from "../../actions/entityActiveInactiveAction";
import { toast } from "react-toastify";
import { downloadParentCodeData } from "../../utils/downloadParentCodeData";
import TableView from "./Components/TableView";
import RelationshipView from "./Components/RelationshipView";
import "./index.scss";

const ITEMS_PER_PAGE = 8;
const SIDEBAR_PAGE_SIZE = 10;

const Field = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  placeholder,
  readOnly,
}) => (
  <InputField
    label={label}
    name={name}
    type="text"
    value={value}
    onChange={onChange}
    error={error}
    required={required}
    placeholder={placeholder}
    readOnly={readOnly}
  />
);

const EditIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const MoreIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
);

// ── Dropdown action menu ───────────────────────────────────────────────────────
const ActionMenu = ({ onEdit, onView }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="pc-action-menu" ref={ref}>
      <button
        className="pc-icon-btn pc-icon-btn--more"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreIcon />
      </button>
      {open && (
        <div className="pc-action-menu__dropdown">
          <button
            className="pc-action-menu__item"
            onClick={() => {
              onView();
              setOpen(false);
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View
          </button>
          <button
            className="pc-action-menu__item"
            onClick={() => {
              onEdit();
              setOpen(false);
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const ParentCode = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeView, setActiveView] = useState("table");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewingChild, setViewingChild] = useState(null);
  const [sidebarPage, setSidebarPage] = useState(1);

  // ── Status modal state ─────────────────────────────────────────────────────
  const [statusModal, setStatusModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // the child card
  const [statusValue, setStatusValue] = useState(true); // toggle: true=active
  const [statusLoading, setStatusLoading] = useState(false);

  const openStatusModal = (child) => {
    setStatusTarget(child);
    setStatusValue(child.isActive === true); // seed from current status
    setStatusModal(true);
  };

  const closeStatusModal = () => {
    setStatusModal(false);
    setStatusTarget(null);
  };

  const handleStatusSubmit = async () => {
    if (!statusTarget) return;
    // Use the numeric record id (child.id from API) as entityPrimaryId
    const id = Number(statusTarget.id);
    if (!id) {
      toast.error("Entity ID not found");
      return;
    }
    setStatusLoading(true);
    const result = await entityActiveInactiveAction(id, statusValue);
    setStatusLoading(false);
    if (result.success) {
      toast.success(result.message || "Status updated!");
      closeStatusModal();
      await reloadData(); // re-fetch only the company/child data
    } else {
      toast.error(result.message || "Failed to update status");
    }
  };

  const {
    parents,
    children,
    allChildren,
    isLoading,
    selectedParent,
    setSelectedParent,
    reloadData,
    parentModal,
    editingParent,
    openParentModal,
    openEditParentModal,
    closeParentModal,
    parentForm,
    parentErrors,
    handleParentChange,
    submitParent,
    childModal,
    editingChild,
    openChildModal,
    openEditChildModal,
    closeChildModal,
    childForm,
    childErrors,
    handleChildChange,
    submitChild,
  } = useParentCodeData();

  const handleSelectParent = (code) => {
    setSelectedParent(code);
    setCurrentPage(1);
    setSearchQuery("");
  };

  // Sidebar pagination
  const sidebarTotalPages = Math.ceil(parents.length / SIDEBAR_PAGE_SIZE);
  const pagedParents = parents.slice(
    (sidebarPage - 1) * SIDEBAR_PAGE_SIZE,
    sidebarPage * SIDEBAR_PAGE_SIZE,
  );

  const filteredChildren = children.filter(
    (c) =>
      c.childName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.childCode.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const totalItems = filteredChildren.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const pagedChildren = filteredChildren.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const companyOptions = [
    { value: "", label: "Select company" },
    ...parents.map((p) => ({ value: p.parentCode, label: p.parentName })),
  ];

  return (
    <div className="pc-page">
      {/* Header */}
      <div className="pc-header">
        <div>
          <h1 className="pc-header__title">Company &amp; Distributor</h1>
          <p className="pc-header__sub">Manage companies and distributors</p>
        </div>
        {/* download all active / inactive data in the excel sheet */}
        <button
          className="pc-header__btn"
          onClick={() => downloadParentCodeData(allChildren, parents)}
        >
          {/* Download All Inactive/Active */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Excel Download
        </button>
      </div>

      {/* View toggle */}
      <div className="pc-view-toggle">
        <div>
          <button
            className={`pc-view-toggle__btn${activeView === "table" ? " pc-view-toggle__btn--active" : ""}`}
            onClick={() => setActiveView("table")}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
            Table View
          </button>
          <button
            className={`pc-view-toggle__btn${activeView === "relationship" ? " pc-view-toggle__btn--active" : ""}`}
            onClick={() => setActiveView("relationship")}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="6" cy="12" r="2" />
              <circle cx="18" cy="6" r="2" />
              <circle cx="18" cy="18" r="2" />
              <line x1="8" y1="11" x2="16" y2="7" />
              <line x1="8" y1="13" x2="16" y2="17" />
            </svg>
            Relationship View
          </button>
        </div>
      </div>

      {/* Active view */}
      {activeView === "table" ? (
        <TableView
          parents={parents}
          children={children}
          allChildren={allChildren}
          isLoading={isLoading}
          selectedParent={selectedParent}
          setSelectedParent={setSelectedParent}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          openStatusModal={openStatusModal}
        />
      ) : (
        <RelationshipView />
      )}

      {/* Company Modal */}
      <Modal
        isOpen={parentModal}
        onClose={closeParentModal}
        title={editingParent ? "Edit Company" : "Create Company"}
        size="medium"
      >
        <div className="pc-modal-form">
          <Field
            label="Company Code"
            name="parentCode"
            value={parentForm.parentCode}
            onChange={handleParentChange}
            error={parentErrors.parentCode}
            required
            placeholder="e.g. ABC0001"
            readOnly={!!editingParent}
          />
          <Field
            label="Company Name"
            name="parentName"
            value={parentForm.parentName}
            onChange={handleParentChange}
            error={parentErrors.parentName}
            required
            placeholder="Enter company name"
          />
          <Field
            label="Description"
            name="description"
            value={parentForm.description}
            onChange={handleParentChange}
            placeholder="Optional description"
          />
          {/* <DropDown
            label="Status"
            name="status"
            value={parentForm.status}
            onChange={handleParentChange}
            options={statusOptions}
            fullWidth
          /> */}
          <div className="pc-modal-actions">
            <button className="btn-cancel" onClick={closeParentModal}>
              Cancel
            </button>
            <button className="btn-submit" onClick={submitParent}>
              {editingParent ? "Save Changes" : "Create"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Distributor Modal */}
      <Modal
        isOpen={childModal}
        onClose={closeChildModal}
        title={editingChild ? "Edit Distributor" : "Create Distributor"}
        size="medium"
      >
        <div className="pc-modal-form">
          <DropDown
            label="Company"
            name="parentCode"
            value={childForm.parentCode}
            onChange={handleChildChange}
            options={companyOptions}
            error={childErrors.parentCode}
            required
            fullWidth
          />
          <Field
            label="Distributor Code"
            name="childCode"
            value={childForm.childCode}
            onChange={handleChildChange}
            error={childErrors.childCode}
            required
            placeholder="e.g. ABC0001-D01"
            readOnly={!!editingChild}
          />
          <Field
            label="Distributor Name"
            name="childName"
            value={childForm.childName}
            onChange={handleChildChange}
            error={childErrors.childName}
            required
            placeholder="Enter distributor name"
          />
          <Field
            label="Description"
            name="description"
            value={childForm.description}
            onChange={handleChildChange}
            placeholder="Optional description"
          />
          <DropDown
            label="Status"
            name="status"
            value={childForm.status}
            onChange={handleChildChange}
            options={statusOptions}
            fullWidth
          />
          <div className="pc-modal-actions">
            <button className="btn-cancel" onClick={closeChildModal}>
              Cancel
            </button>
            <button className="btn-submit" onClick={submitChild}>
              {editingChild ? "Save Changes" : "Create"}
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      {viewingChild && (
        <Modal
          isOpen={!!viewingChild}
          onClose={() => setViewingChild(null)}
          title="Distributor Details"
          size="medium"
        >
          <div className="pc-view-modal">
            <div className="pc-view-row">
              <span>Distributor Code</span>
              <strong>{viewingChild.childCode}</strong>
            </div>
            <div className="pc-view-row">
              <span>Distributor Name</span>
              <strong>{viewingChild.childName}</strong>
            </div>
            <div className="pc-view-row">
              <span>Company</span>
              <strong>{viewingChild.parentCode}</strong>
            </div>
            <div className="pc-view-row">
              <span>Description</span>
              <strong>{viewingChild.description || "—"}</strong>
            </div>
            <div className="pc-view-row">
              <span>Status</span>
              <span className={`pc-status pc-status--${viewingChild.status}`}>
                {viewingChild.status}
              </span>
            </div>
          </div>
        </Modal>
      )}

      {/* Change Status Modal */}
      <Modal
        isOpen={statusModal}
        onClose={closeStatusModal}
        title="Change Status"
        size="small"
      >
        <div className="pc-status-modal">
          {/* Entity info strip */}
          <div className="pc-status-modal__info">
            <div className="pc-status-modal__avatar">
              {(statusTarget?.childName || "?")[0].toUpperCase()}
            </div>
            <div className="pc-status-modal__info-text">
              <p className="pc-status-modal__entity-name">
                {statusTarget?.childName || "—"}
              </p>
              <p className="pc-status-modal__entity-id">
                {statusTarget?.entityId || statusTarget?.id || ""}
              </p>
            </div>
          </div>

          <div className="pc-status-modal__divider" />

          {/* Toggle section */}
          <div className="pc-status-modal__section">
            <p className="pc-status-modal__section-label">Active Status</p>
            <p className="pc-status-modal__section-hint">
              Toggle to set whether this entity is active or inactive.
            </p>

            <div className="pc-status-modal__toggle-wrap">
              <button
                className={`pc-status-option ${!statusValue ? "pc-status-option--inactive" : ""}`}
                onClick={() => setStatusValue(false)}
              >
                <span className="pc-status-option__dot" />
                Inactive
              </button>

              <button
                className={`pc-toggle ${statusValue ? "pc-toggle--on" : "pc-toggle--off"}`}
                onClick={() => setStatusValue((v) => !v)}
                aria-label="Toggle active status"
              >
                <span className="pc-toggle__thumb" />
              </button>

              <button
                className={`pc-status-option ${statusValue ? "pc-status-option--active" : ""}`}
                onClick={() => setStatusValue(true)}
              >
                <span className="pc-status-option__dot" />
                Active
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="pc-status-modal__actions">
            <button
              className="pc-status-modal__btn-cancel"
              onClick={closeStatusModal}
            >
              Cancel
            </button>
            <button
              className="pc-status-modal__btn-confirm"
              onClick={handleStatusSubmit}
              disabled={statusLoading}
            >
              {statusLoading && <span className="pc-status-modal__spinner" />}
              {statusLoading ? "Saving…" : "Confirm"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ParentCode;
